/**
 * Structural sponsor-wall data for Bursa Bull Charge × The Edge KL Rat Race 2026.
 *
 * Company names, websites and logo files are FACTS, not translatable copy, so
 * they live here — same rule as src/pages/sponsorship/data.ts. Only headings,
 * tier labels and CTA copy go to i18next (namespace "sponsors").
 *
 * ADDING A SPONSOR = one object literal in the right tier's `sponsors` array.
 * Drop the logo in public/sponsor-logos/<tier>/, kebab-case, lowercase
 * extension, no spaces or parentheses (public/ is copied verbatim by Vite, so
 * the filename IS the URL).
 *
 * FIRST, CROP THE FILE TO ITS INK BOUNDS. This is load-bearing, not tidiness:
 * SponsorWall sizes every logo from its aspect ratio and its TIER alone (see
 * LOGO_FILL and TIER_LOGO_SCALE), so baked-in whitespace is indistinguishable
 * from the mark and the logo renders proportionally smaller — which now also
 * reads as the wrong billing tier. Two files have already shipped broken this
 * way — The Edge Malaysia and SD Guthrie both arrived on a 1920x1080 canvas
 * with the mark filling <30% of the height, and a re-exported Mah Sing was 70%
 * whitespace. Keep the uncropped file in ../../../sponsor-source-originals/<tier>/.
 *
 * THEN record the CROPPED file's real pixel dimensions in width/height. A stale
 * pair is silently wrong: it feeds the inline aspect-ratio, so the box shape
 * stops matching the image and object-contain letterboxes the mark.
 */

/**
 * Wall order, top → bottom. `organisers` are the bodies that RUN the event
 * (Bursa × The Edge), not sponsors, so they lead. `officialBank` is a
 * category-exclusive naming right that also sits OUTSIDE the paid ladder; the
 * ladder then reads descending prestige. Three category partners follow
 * `supporting`, in the client's order: `officialTvRadio`, `officialOutdoorTv`,
 * then `officialHydration`. The first two are the media partners and share ONE
 * wall row, TV & Radio on the left (see PAIRED_ROWS in SponsorWall.tsx — they
 * must stay adjacent here for that to work). `inKind` (donated goods/services)
 * closes the wall — it is not a rung on the paid ladder either, so it is placed
 * last by convention rather than by price.
 *
 * This order is also the LOGO SIZE order — but rungs may TIE, and two groups
 * deliberately do: `organisers` + `officialBank` share the top rung, and
 * `supporting`, `officialTvRadio`, `officialOutdoorTv`, `officialHydration`
 * and `inKind` all render at the same size (client instruction — an in-kind
 * sponsor is billed alongside a supporting partner, not below one; the three
 * category partners sit between them, so they can only tie).
 * What the ladder guarantees is that a tier never renders BIGGER than one above
 * it. The multipliers live in TIER_LOGO_SCALE in SponsorWall.tsx; add a tier
 * here and it needs a rung there (TypeScript enforces it).
 *
 * NOTE this is display order, deliberately the REVERSE of
 * sponsorship/data.ts's `monetaryPackages` (ascending price, flipped at render).
 * Don't "fix" it to match.
 */
export const SPONSOR_TIER_IDS = [
  "organisers",
  "officialBank",
  "platinum",
  "gold",
  "silver",
  "bronze",
  "supporting",
  "officialTvRadio",
  "officialOutdoorTv",
  "officialHydration",
  "inKind"
] as const;
export type SponsorTierId = (typeof SPONSOR_TIER_IDS)[number];

export interface Sponsor {
  /** Stable kebab-case id — React key only. */
  id: string;
  /** Brand name. Proper noun → never translated. Doubles as the img alt text. */
  name: string;
  /** Sponsor website, absolute https. Opens in a new tab. */
  href: string;
  /** Site-absolute path under public/sponsor-logos/. */
  logo: string;
  /**
   * Intrinsic pixel size of the CROPPED file. Reserves the box (CLS = 0), feeds
   * the inline aspect-ratio that works around the Safari flex-`img` bug
   * documented in components/layout/Header.tsx, and — because the file is
   * cropped to ink — is also what SponsorWall's equal-area sizing measures.
   */
  width: number;
  height: number;
  /**
   * Escape hatch for ONE logo, defaults to 1. Equal AREA is the right default
   * within a tier, but a very dense mark (solid block) can still read heavier
   * than an airy one at the same area. Nudge that one logo (e.g. 0.92) rather
   * than re-tuning the global scale.
   *
   * This MULTIPLIES the tier's own scale (see TIER_LOGO_SCALE in
   * SponsorWall.tsx), so it stays relative to the logo's own tier — it is not
   * the way to change a whole tier's billing size.
   *
   * Reach for this only after cropping — a logo that looks small is almost
   * always an uncropped canvas, not a scale problem.
   */
  opticalScale?: number;
  /**
   * OPTIONAL — the box of the logo's MARK, in cropped-file pixels: the part
   * the client wants read at full size, when the file also carries a
   * secondary element. Three shapes of it are in use (client requests, Sep
   * 2026):
   *   - a name/endorsement line UNDER the mark — Gas Malaysia's and Malakoff's
   *     "A Member of MMC Group", F&N's legal-entity line, "PUBLIC BANK" under
   *     its emblem;
   *   - decoration AROUND the mark — UEM Sunrise's loops;
   *   - a symbol BESIDE the wordmark — Axiata's chevron.
   *
   * When set, sizing measures THIS box instead of the whole file, and
   * SponsorWall shifts the image so the MARK — not the image — is centred on
   * the tile, keeping it level with the row; everything else extends above,
   * below or around it. The width cap still uses the WHOLE image, and a DEV
   * guard warns if the result would leave its tile at any breakpoint.
   *
   * Marked logos then read heavy (the extra element adds weight), so each also
   * sets `opticalScale` to match its tier's median visible ink — see the
   * entries and CLAUDE.md.
   *
   * A two-line wordmark (TEEF, The Edge Malaysia) is ONE mark: leave this
   * unset. Measure the box from the file (largest empty band, colour/hue
   * mask, or erosion + blobs) and check it by eye; never guess it.
   */
  mark?: { x: number; y: number; width: number; height: number };
  /**
   * OPTIONAL — the sponsor's promo video, produced by The Edge (the `promoVideo`
   * perk in sponsorship/data.ts). Its PRESENCE is what puts a sponsor on
   * /sponsors/videos (see `sponsorVideos` below), so adding the next delivered
   * video is a single object here and nothing else.
   *
   * `src`/`poster` are site-absolute paths under public/sponsor-videos/, named
   * after the sponsor id: kebab-case, lowercase, no spaces. public/ is copied
   * verbatim so the filename IS the URL, and S3 serves asset paths
   * case-sensitively (the CloudFront function only lowercases extensionless
   * URIs), so an uppercase file would 404 unless uploaded byte-identically.
   *
   * The MP4 must be "fast start" — its index (moov) BEFORE the media (mdat) —
   * or a browser has to fetch the tail of a 40MB file before it can play a
   * frame. Re-wrap losslessly, no re-encode:
   *   avconvert --preset PresetPassthrough --source in.mp4 --output out.mp4
   * Poster stills come from the video itself (qlmanage -t, then sips to JPEG).
   *
   * `durationSec` is read off the file (mdls -name kMDItemDurationSeconds) and
   * feeds both the badge on the card and the ISO-8601 duration in the JSON-LD.
   * These run ~50s, NOT the 20s the sponsorship deck promises — don't restate a
   * duration in copy; the badge is generated from this number.
   */
  promoVideo?: { src: string; poster?: string; durationSec: number };
}

export interface SponsorTier {
  id: SponsorTierId;
  sponsors: Sponsor[];
}

/**
 * Within a tier, list alphabetically — no sponsor gets implied precedence.
 *
 * EXCEPT `organisers`, which is a client-specified sequence (see the tier's own
 * note). Don't re-sort it.
 */
export const sponsorTiers: SponsorTier[] = [
  {
    // The organising bodies, not sponsors. Excluded from `allSponsors` (and so
    // from the sponsors ItemList JSON-LD); they're declared as schema.org
    // `organizer` on the event instead — see seo/structuredData.ts, whose
    // `organizer` array mirrors this ORDER and must be kept in step.
    //
    // Order is the client's, not alphabetical: each corporate family runs
    // parent then foundation (Bursa → Yayasan Bursa, The Edge → TEEF).
    id: "organisers",
    sponsors: [
      {
        id: "bursa-malaysia",
        name: "Bursa Malaysia",
        href: "https://www.bursamalaysia.com",
        logo: "/sponsor-logos/organiser/bursa-malaysia.png",
        width: 1149,
        height: 339
      },
      {
        id: "yayasan-bursa-malaysia",
        name: "Yayasan Bursa Malaysia",
        // No standalone domain — YBM lives as a section of the Bursa site.
        href: "https://www.bursamalaysia.com/about_bursa/sustainability/yayasan-bursa-malaysia/overview",
        logo: "/sponsor-logos/organiser/yayasan-bursa-malaysia.png",
        width: 766,
        height: 279
      },
      {
        id: "the-edge-malaysia",
        name: "The Edge Malaysia",
        href: "https://theedgemalaysia.com",
        // Source art sat in a 16:9 canvas with the mark filling only 27% of the
        // height; cropped to its ink bounds so it reads at the same weight as
        // the rest of the wall.
        logo: "/sponsor-logos/organiser/the-edge-malaysia.png",
        width: 900,
        height: 181
      },
      {
        id: "the-edge-education-foundation",
        name: "The Edge Education Foundation",
        href: "https://teef.org.my/",
        logo: "/sponsor-logos/organiser/the-edge-education-foundation.png",
        // Low-res source (276x72) — slightly soft on retina. Drop a larger file
        // at the same path and update these dimensions; nothing else changes.
        width: 276,
        height: 72
      }
    ]
  },
  {
    id: "officialBank",
    sponsors: [
      {
        id: "uob-malaysia",
        name: "UOB Malaysia",
        href: "https://www.uob.com.my/",
        // Carries a hard white matte rather than transparency — see the bg-card
        // note in SponsorWall.tsx for why the tile must stay pure white.
        logo: "/sponsor-logos/official/uob-malaysia.png",
        width: 900,
        height: 256
      }
    ]
  },
  { id: "platinum", sponsors: [] },
  {
    // The en label is SINGULAR ("Gold Sponsor") because this tier holds ONE
    // sponsor — client instruction, Sep 2026. Add a second and restore the "s"
    // in i18n/locales/en/sponsors.json. BM needs no change: Malay doesn't mark
    // plural. Same rule on `silver` and `inKind`.
    id: "gold",
    sponsors: [
      {
        id: "ytl-group",
        // The wordmark as drawn ("YTL GROUP"). ytl.com titles itself "YTL
        // Corp", but the wall uses the name on the lockup, like SD Guthrie.
        name: "YTL Group",
        // www, not the apex: ytl.com 301s here (same reasoning as AEON).
        href: "https://www.ytl.com/",
        /*
         * The FIRST GOLD SPONSOR: filling this tier is what makes the "Gold
         * Sponsors" heading appear, between Official Bank and Silver. Its rung
         * (0.83), tone and i18n labels were already in place.
         *
         * Supplied as a one-page PDF ("YTL Group_Primary Logo_Final.pdf", kept
         * in ../../../sponsor-source-originals/gold/) with the lockup centred
         * on a 1440x900pt canvas:
         *
         *   pdftocairo -png -singlefile -transp -r 200 "<pdf>" out
         *
         * then crop to the ALPHA channel's bbox (getchannel("A").getbbox(), not
         * the RGBA one — the trap documented for SIME). 200dpi gives 1936px;
         * the largest render is ~190px wide, so that is ~3.4x a 3x-DPR display.
         *
         * PARTIAL WHITE MATTE. The wordmark and tagline sit on true
         * transparency, but the emblem (grey bars over the blue "YTL" block)
         * sits on an opaque white plate — the gaps between the bars are white,
         * not clear. Invisible only because the sponsor sections are pure white;
         * see WALL_SURFACE in SponsorWall.tsx.
         *
         * At the gold rung the tagline ("Building The Right Thing") is roughly
         * legible, unlike the lower tiers, so it ships as supplied.
         *
         * 3.81:1 gives a span of 0.83 * sqrt(3.811) = 1.62, under the binding
         * caps (The Edge Malaysia 2.23 on the wall, UOB 1.875 on the strip), so
         * no existing logo resizes.
         */
        logo: "/sponsor-logos/gold/ytl-group.png",
        width: 1936,
        height: 508
      }
    ]
  },
  {
    // SINGULAR en label ("Silver Sponsor") while this tier holds one sponsor —
    // see the note on `gold` above.
    id: "silver",
    sponsors: [
      {
        id: "sd-guthrie",
        // The wordmark as it appears in the logo. The registered entity is
        // "SD Guthrie Berhad", but the wall uses trading names throughout
        // (Vistra, UOB Malaysia) — don't expand it.
        name: "SD Guthrie",
        href: "https://www.sdguthrie.com/",
        // Supplied on a 1920x1080 canvas with the mark filling 29% of the
        // height; cropped to its ink bounds like The Edge Malaysia was.
        logo: "/sponsor-logos/silver/sd-guthrie.png",
        width: 1034,
        height: 314
      }
    ]
  },
  {
    id: "bronze",
    sponsors: [
      {
        id: "dcd-technology",
        name: "DCD Technology",
        href: "https://www.dcdtech.com.my/",
        logo: "/sponsor-logos/bronze/dcd-technology-banner.png",
        /*
         * The WIDE BANNER lockup (monogram + "TECHNOLOGY" + tagline), 6.94:1 —
         * supplied Aug 2026 as `DCD info V1.pdf` and shipped at the client's
         * explicit instruction to size it "similar to the LFG".
         *
         * THE TRADE-OFF IS KNOWN AND WAS ACCEPTED. Do not re-litigate it, and do
         * not "fix" it with an opticalScale nudge. Equal-area sizing turns width
         * into SHORTNESS (height ∝ 1/√ratio), so at 6.94:1 in bronze this renders
         * ~25px tall on the wall and ~18px in the footer, against ~45px/~33px for
         * the 2.14:1 stacked badge it replaces. The tagline lands ~6px and is not
         * legible at either size. The client was shown these numbers and chose to
         * ship anyway.
         *
         * The LFG comparison is what justifies it, and it holds: LFG is 8.98:1 in
         * supporting (0.45) and renders 17.6px / 13.1px. Bronze sits a rung above,
         * so this clears LFG by ~41% on both surfaces. A wide-banner precedent was
         * already live and accepted on the page.
         *
         * A near-identical banner at 6.19:1 WAS shipped and reverted once before,
         * for exactly the smallness above — so if the client complains about size
         * again, the answer is a stacked/compact lockup from DCD (~2.5:1 would
         * render ~42px), NOT a scale nudge here, which would break tier equality.
         * Both older files are kept in ../../../sponsor-source-originals/bronze/.
         *
         * Unlike its predecessors this file has GENUINE ALPHA, not a white matte.
         * Palette-quantized (P + tRNS, 31KB vs 135KB RGBA); verified invisible at
         * render size — max 3-6/255 on <0.5% of pixels, core alpha 254/255.
         *
         * No opticalScale: √6.937 × 0.56 = 1.47, well under the binding caps
         * (Edge Malaysia 2.23 wall / UOB 1.88 strip), so nothing else shrinks.
         */
        width: 1873,
        height: 270
        /*
         * Promo video withdrawn 8 Oct 2026 (see CLAUDE.md, "Microsite Sponsor
         * Videos"). Only the video: DCD remains a Bronze sponsor on the wall and
         * the footer strip. Comments here ship in the public source map, so keep
         * this note factual.
         *
         * The files were moved OUT of public/ (so no deploy re-uploads them) to
         * ../../../sponsor-source-originals/videos/, and deleted from the prod
         * bucket by hand (the upload script never deletes). To restore, git mv
         * them back to public/sponsor-videos/ and re-add:
         *
         *   promoVideo: {
         *     src: "/sponsor-videos/dcd-technology-720.mp4",
         *     poster: "/sponsor-videos/dcd-technology-poster.jpg",
         *     durationSec: 54
         *   }
         */
      },
      {
        id: "mah-sing-group",
        name: "Mah Sing Group",
        href: "https://www.mahsing.com.my/",
        // Re-exported at 1774x887 (70% whitespace) after the first release,
        // which left these dimensions stale and shrank the mark on the wall.
        // Now cropped to ink; both source exports are kept in
        // sponsor-source-originals/bronze/.
        logo: "/sponsor-logos/bronze/mah-sing-group.png",
        width: 1133,
        height: 270,
        promoVideo: {
          src: "/sponsor-videos/mah-sing-group-720.mp4",
          poster: "/sponsor-videos/mah-sing-group-poster.jpg",
          durationSec: 49
        }
      },
      {
        id: "vistra",
        name: "Vistra",
        href: "https://www.vistra.com/",
        // Widest FILE on the wall at 5.34:1, but no longer what caps the sizing:
        // bronze renders at 0.71, so its effective span drops below the top-rung
        // organiser marks. See maxRootSpan in SponsorWall.tsx — the cap is
        // derived, so a wider logo anywhere just recomputes it.
        logo: "/sponsor-logos/bronze/vistra.png",
        width: 769,
        height: 144
      }
    ]
  },
  {
    id: "supporting",
    sponsors: [
      {
        id: "aeon",
        // AEON Co. (M) Bhd — the retail/mall operator. The supplied ÆON mark is
        // shared across the AEON group, so it is NOT interchangeable with AEON
        // Credit Service (M) Bhd, a separately listed company. Client-confirmed.
        name: "AEON",
        // The apex, not www.aeonretail.com.my — that host 301s here, so linking
        // it would cost every visitor a redirect hop for nothing.
        href: "https://aeonretail.com.my/",
        /*
         * The POSITIVE colour lockup: a magenta (#B1006F) ÆON wordmark with its
         * gradient ring, on genuine transparency. It replaced the earlier
         * magenta PLATE with the wordmark knocked out in white. The client
         * supplied it in Sep 2026 as `AEON_Logo_Colour.png`. The plate is kept
         * in ../../../sponsor-source-originals/support/ as
         * aeon-previous-plate-549x188.png, next to its own uncropped original.
         *
         * NEW URL ON PURPOSE (aeon-colour.png, not aeon.png): public/ assets
         * are not content-hashed. The deploy invalidates CloudFront, but a
         * browser that cached the old path could keep showing the plate.
         *
         * The supplied file is already tight to its ink (alpha bbox = the full
         * 3207x960 canvas), so there was nothing to crop. It was LANCZOS-
         * downscaled (premultiplied) to 1200 wide: the largest render anywhere
         * is ~96px wide (xl band), so 1200 is still ~4x a 3x-DPR display, and it
         * decodes to ~1.7MB instead of ~12MB.
         *
         * 3.34:1 gives a span of 0.45 * sqrt(3.343) = 0.82, far under the
         * binding caps (The Edge Malaysia 2.23 wall / UOB 1.875 strip), so no
         * existing logo resizes.
         */
        logo: "/sponsor-logos/support/aeon-colour.png",
        width: 1200,
        height: 359
      },
      {
        id: "axiata",
        name: "Axiata",
        href: "https://www.axiata.com/",
        /*
         * The ONLY logo on the wall that arrived already cropped to its ink
         * bounds (100% fill on both axes), which is why nothing sits in
         * ../../../sponsor-source-originals/support/ for it — this file IS the
         * supplied original, byte for byte. That gap is deliberate, not an
         * omission.
         *
         * LOWEST INK COVERAGE ON THE WALL — 24% of its own box, against 76% for
         * DIALOG and 58% for Sime — because the lockup is DIAGONAL: chevron
         * mark top-right, "axiata" wordmark bottom-left, two genuinely empty
         * corners. Equal-area sizing measures the BOX, so this reads lighter
         * than its tier-mates at the same nominal area even though it is the
         * TALLEST of them (68 x 41px on the wall, vs DIALOG's 78 x 36).
         *
         * That was the artwork, not a missed crop, and it still ships as
         * supplied. Since 2026-09-22, at client request, it is SIZED BY ITS
         * WORDMARK (see `mark` below — the "axiata" letters, measured by hue):
         * the wordmark is centred on the tile and the chevron sits above-right.
         * opticalScale then matches the tier's median visible ink, which LIFTS
         * this light logo: wordmark ~59x16 -> ~75x21, whole logo ~68x41 ->
         * ~87x52 on the xl wall.
         *
         * 1.66:1 gives a span of 0.45 * sqrt(1.660) = 0.58 — far under the
         * binding caps (The Edge Malaysia 2.23 on the wall, UOB 1.875 on the
         * strip), so nothing else resizes.
         *
         * Kept RGBA rather than palette-quantized like DCD's banner: the
         * chevron is faceted gradients (1715 unique RGBA values) and a
         * 255-colour octree pass measured up to 30/255 error against DCD's
         * accepted 3-6. It would save 58KB and is not worth the banding.
         */
        logo: "/sponsor-logos/support/axiata.png",
        width: 2674,
        height: 1611,
        // The "axiata" wordmark (hue ~217° pixels); the chevron is excluded.
        mark: { x: 0, y: 966, width: 2311, height: 645 },
        // Matched to the 0.45 tier's median visible ink (~1100px² on the xl
        // wall), Sep 2026: marked logos read heavy because the extra element
        // adds weight. 0.75 = sqrt(1100 / 1937). Re-measure before changing.
        opticalScale: 0.75
      },
      {
        id: "dialog",
        name: "Dialog Group",
        href: "https://www.dialogasia.com/",
        /*
         * Converted from a CMYK print PDF (kept in
         * ../../../sponsor-source-originals/support/), rendered at 200dpi and
         * cropped to ink. CMYK -> RGB shifts colour slightly; the teal lands at
         * #009090. The white inside the wordmark and between the rules is a
         * MATTE, not transparency — invisible only because the sponsor sections
         * are pure white. See WALL_SURFACE in SponsorWall.tsx.
         */
        logo: "/sponsor-logos/support/dialog.png",
        width: 1711,
        height: 787,
        // Client asked for "slightly larger" (2026-09-22). 1.10 is capped by
        // consistency: it lifts DIALOG's visible ink to ~2480px², just under
        // Himalaya's 2501 (the heaviest plate-style logo on this rung), and its
        // width to ~86px, level with Axiata. Don't go higher without re-measuring.
        opticalScale: 1.1
      },
      {
        id: "gas-malaysia",
        // Trading name, as on the lockup. The registered entity is "Gas
        // Malaysia Berhad", but the wall uses trading names throughout.
        name: "Gas Malaysia",
        // www, not the apex: gasmalaysia.com 301s here. The site bot-blocks
        // automated checks (403 / "Access Denied"), so the domain was confirmed
        // from its TLS certificate instead: O=Gas Malaysia Berhad, Shah Alam.
        href: "https://www.gasmalaysia.com/",
        /*
         * THE FULL LOCKUP, MMC ENDORSEMENT INCLUDED, AS SUPPLIED — do not crop
         * the "A Member of MMC Group" line off. The client chose this on
         * 2026-09-14 after seeing the numbers below, the same call as UEM
         * Sunrise.
         *
         * SIZED BY ITS MARK (see `mark` below) since 2026-09-22, at client
         * request from their mockup. The endorsement line (8.35:1) spans the
         * full width while the Gas Malaysia mark above it is only a third of
         * it, so whole-file sizing left the mark ~22px wide. Now the MARK
         * (symbol + "GAS MALAYSIA") is what gets sized, and the MMC line hangs
         * below. At full mark size it read ~1.4x its tier-mates' visual weight
         * (the name line adds weight), so opticalScale trims it back to the
         * tier's median visible ink (0.83), then nudged up to 0.90 at client
         * request: mark ~41x55px, whole logo ~126x76px on the xl wall.
         *
         * Supplied as "Logo Gas Malaysia Berhad.png" on a 3508x2480 A4 canvas
         * at 300dpi (kept in ../../../sponsor-source-originals/support/).
         * Cropped to the ALPHA bbox (3314x1998), then LANCZOS-downscaled
         * (premultiplied) to 1200 wide: the largest render is ~126px wide, so
         * 1200 is ~3.2x a 3x-DPR display. Genuine alpha, no white matte.
         *
         * Mark-sized span: 0.45 * 1200 / sqrt(394 * 522) = 1.19, still under
         * the binding caps, so no other logo resizes.
         */
        logo: "/sponsor-logos/support/gas-malaysia.png",
        width: 1200,
        height: 723,
        // The symbol + "GAS MALAYSIA" block; the MMC line below is excluded.
        mark: { x: 412, y: 0, width: 394, height: 522 },
        // Matched to the 0.45 tier's median visible ink (~1100px² on the xl
        // wall), Sep 2026: marked logos read heavy because the name line adds
        // weight. sqrt(1100 / 1589) = 0.83, nudged up to 0.90 at client request
        // (2026-09-22, "slightly larger"). Re-measure before changing.
        opticalScale: 0.9
      },
      {
        id: "lianson-fleet-group",
        name: "Lianson Fleet Group",
        href: "https://lianson.com/",
        /*
         * The positive lockup, which unblocked this: the earlier supply was the
         * WHITE/reversed artwork (still in ../../../sponsor-source-originals/
         * support/LFG_Full_Logo_White_FA.ai), invisible on a white surface.
         * Cropped from a supplied 1920x400 JPEG in which the mark filled only
         * 48% of the height, and re-saved as PNG to match the rest of the set.
         * Like DIALOG this is a white MATTE, not transparency — invisible only
         * because the sponsor sections are pure white (see WALL_SURFACE).
         *
         * WIDEST MARK ON THE SITE at 8.98:1, and that is why it renders SHORT:
         * equal area splits between width and height, so ~17.6px tall on the
         * wall and ~9.5px in the mobile footer — about half its tier-mate
         * DIALOG despite the same rung. The client accepted that over cropping
         * to the LFG monogram (3.64:1, which would render ~27.7px). Do not try
         * to fix it with scale: the ratio is the cause, and only a stacked
         * lockup from LFG changes it.
         *
         * It does NOT constrain anything: its effective span is
         * 0.45 * sqrt(8.98) = 1.35, well under the wall's binding 2.23 and the
         * strip's 1.875 — so ROOT_SPAN_MAX is untouched and no other logo
         * resizes. The wide-logo DEV warning staying silent is correct: that
         * threshold is on the SCALED span, by design.
         */
        logo: "/sponsor-logos/support/lianson-fleet-group.png",
        width: 1724,
        height: 192
      },
      {
        id: "malakoff",
        // Trading name, as on the lockup ("malakoff"). The registered entity is
        // "Malakoff Corporation Berhad"; the wall uses trading names throughout.
        // Like Gas Malaysia, it is "A Member of MMC Group". Two sponsors share
        // a parent, but they are separate companies, so don't merge them.
        name: "Malakoff",
        // The site sits behind a Cloudflare bot challenge (403 "Just a moment..."
        // to curl), so a HEAD/GET link checker will flag this as broken. It
        // isn't: in a real browser www serves the corporate site directly, no
        // redirect (checked 2026-10-14). The TLS cert is CN=malakoff.com.my,
        // SAN *.malakoff.com.my.
        href: "https://www.malakoff.com.my/",
        /*
         * Placed before Public Bank at client request (2026-10-14), which is
         * also its alphabetical slot.
         *
         * Supplied as an Illustrator file ("Malakoff_MMC_Logo_FA_Full Colour_
         * CMYK_230615 (1).ai", kept in ../../../sponsor-source-originals/
         * support/). It is PDF-compatible, so it takes the Public Bank route:
         *
         *   pdftocairo -png -singlefile -transp -r 120 "<ai>" out
         *
         * then crop to the ALPHA channel's bbox (getchannel("A").getbbox(), not
         * the RGBA one, the trap documented for SIME). 120dpi gives 1182px; the
         * largest render is ~123px wide, so that is ~3.2x a 3x-DPR display.
         * CMYK -> RGB lands the navy wordmark at #1B2D5E.
         *
         * GENUINE ALPHA, no white matte (0 opaque near-white pixels). Left RGBA
         * like Axiata: the antialiased edges carry too many alpha values to
         * palette-quantize cleanly, and 55KB is in line with the set.
         *
         * SIZED BY ITS MARK, the Gas Malaysia pattern: the lockup is the emblem,
         * the "malakoff" wordmark beside it, and an "A Member of MMC Group" line
         * under the wordmark. Unadjusted it rendered ~103x27px on the xl wall
         * at 27% ink, i.e. ~760px² of visible ink against the 0.45 tier's ~1100
         * median: it read small. The emblem runs nearly the full height, so the
         * mark alone lifts it only ~8%. opticalScale then brings it to the tier
         * median: whole logo ~123x32px, wordmark ~16px tall.
         *
         * Mark-sized span: 0.45 * 1182 / sqrt(1182 * 267) = 0.95, under the
         * binding caps (The Edge Malaysia 2.23 wall / UOB 1.875 strip), so no
         * other logo resizes.
         */
        logo: "/sponsor-logos/support/malakoff.png",
        width: 1182,
        height: 311,
        // The emblem + "malakoff" wordmark. The MMC line under the wordmark
        // (rows 221-311) is excluded; the emblem reaches row 267, so the box
        // cannot stop at the line's top edge.
        mark: { x: 0, y: 0, width: 1182, height: 267 },
        // Matched to the 0.45 tier's median visible ink (~1100px² on the xl
        // wall), Oct 2026: ~886px² at full mark size, so 1.11 = sqrt(1100 / 886).
        // Unlike the other marked logos this LIFTS it, because the lockup is
        // mostly air (27% ink). To go bigger, raise it: 1.20 gives ~133x35px.
        // Stay under ~1.5, which keeps it below DIALOG's ~2480px², the
        // heaviest logo on this rung. Re-measure before changing.
        opticalScale: 1.11
      },
      {
        id: "public-bank",
        // Trading name, as on the lockup ("PUBLIC BANK"). The registered entity
        // is "Public Bank Berhad"; the wall uses trading names throughout.
        name: "Public Bank",
        // pbebank.com answers HEAD requests with 404 but serves GET normally,
        // so a HEAD-based link checker will flag this as broken. It isn't.
        href: "https://www.pbebank.com/",
        /*
         * Supplied as an Illustrator file ("Public bank-Logo.ai", kept in
         * ../../../sponsor-source-originals/support/). Modern .ai files are
         * PDF-compatible, so it takes the SIME route:
         *
         *   pdftocairo -png -singlefile -transp -r 200 "Public bank-Logo.ai" out
         *
         * then crop to the ALPHA channel's bbox — the lockup sat in the middle
         * of an A4 artboard. 200dpi gives 1444px; the largest render is ~114px
         * wide, so that is ~4.2x a 3x-DPR display.
         *
         * One flat red (#ED202E) on GENUINE alpha — no white matte, so it does
         * not count against WALL_SURFACE.
         *
         * SIZED BY ITS MARK (see `mark` below) since 2026-09-22: the emblem
         * is what gets sized and "PUBLIC BANK" hangs below. At full mark size
         * it read ~3x its tier-mates' visual weight (a dense emblem plus a big
         * wordmark), so opticalScale trims it to the tier median: emblem
         * ~27x34px, whole logo ~114x50px on the xl wall. Mark-sized span:
         * 0.45 * 1444 / sqrt(346 * 435) = 1.67, under the binding caps, so no
         * other logo resizes.
         */
        logo: "/sponsor-logos/support/public-bank.png",
        width: 1444,
        height: 642,
        // The emblem; the "PUBLIC BANK" wordmark below is excluded.
        mark: { x: 549, y: 0, width: 346, height: 435 },
        // Matched to the 0.45 tier's median visible ink (~1100px² on the xl
        // wall), Sep 2026: marked logos read heavy because the name line adds
        // weight. 0.58 = sqrt(1100 / 3300). Re-measure before changing.
        opticalScale: 0.58
      },
      {
        id: "sime",
        // The wordmark as drawn. The group rebranded from Sime Darby Berhad in
        // Nov 2024 (hence the artwork's "FA_241001"), and sime.com's own title
        // is "Home - Sime" — so the short form is current, not a truncation.
        // NOT the same entity as SD GUTHRIE in the silver tier, which is the
        // former Sime Darby Plantation, nor SIME DARBY PROPERTY, the next entry
        // in this tier. All three are separately listed. Don't "dedupe" them.
        name: "Sime",
        // NOT www.simedarby.com — that host's TLS certificate is expired, so it
        // hard-fails in every browser.
        href: "https://www.sime.com/",
        /*
         * PAGE 2 of the supplied two-page PDF ("Sime logo_CMYK FA_241001", kept
         * in ../../../sponsor-source-originals/support/SIME.pdf). PAGE 1 IS AN
         * ARTWORK SPEC SHEET — job docket and colour swatches, not the logo —
         * so anyone re-rendering this MUST pass `-f 2 -l 2`:
         *
         *   pdftocairo -png -singlefile -transp -r 400 -f 2 -l 2 SIME.pdf out
         *
         * then crop to the ALPHA channel's bbox (`getchannel("A").getbbox()`,
         * not the RGBA one — pdftocairo leaves non-zero RGB under alpha=0, so
         * an RGBA bbox returns the whole canvas and crops nothing). Same route
         * DIALOG took. CMYK -> RGB shifts colour slightly: the navy lands at
         * #1B355C, the orange at #F35C19.
         *
         * GENUINE ALPHA, unlike its tier-mates DIALOG and LFG, which are white
         * mattes. See WALL_SURFACE in SponsorWall.tsx for why that census is
         * load-bearing.
         *
         * Left RGBA rather than palette-quantized like DCD's banner: despite
         * being only two flat spot colours, a 255-colour octree pass measured
         * 13/12/22/30 error on RGBA against DCD's accepted 3-6 — the wordmark's
         * antialiased curves carry too many intermediate alpha values. 41KB is
         * in line with the set anyway.
         *
         * 3.62:1 gives a span of 0.45 * sqrt(3.624) = 0.86, well under the
         * binding caps, so no existing logo changes size.
         */
        logo: "/sponsor-logos/support/sime.png",
        width: 1881,
        height: 519
      },
      {
        id: "sime-darby-property",
        // Trading name, as on the lockup. The registered entity is "Sime Darby
        // Property Berhad", a SEPARATELY LISTED company: not Sime above (the
        // former Sime Darby Berhad) and not SD Guthrie in silver (the former
        // Sime Darby Plantation). Don't "dedupe" any of the three.
        name: "Sime Darby Property",
        // www, not the apex: simedarbyproperty.com 301s here (same reasoning as
        // AEON). The www root then 303s to /home, which is the site's own
        // routing; the root is the stable link.
        href: "https://www.simedarbyproperty.com/",
        /*
         * Placed directly after Sime at client request (2026-10-01), which also
         * keeps the tier alphabetical.
         *
         * Supplied as a 1097x842 PNG at 300dpi (kept in
         * ../../../sponsor-source-originals/support/ as
         * sime-darby-property-supplied-1097x842.png) with a ~31px transparent
         * border: ink filled only 94% x 92% of the canvas. Cropped to the ALPHA
         * bbox (31, 32, 1065, 809), lossless (no resample). The largest render
         * is ~61px wide, so 1034px is already ~5.6x a 3x-DPR display.
         *
         * GENUINE ALPHA: the curved plate is cut out in the alpha channel (the
         * corners are alpha 0). The white "Sime Darby Property" lettering is
         * opaque artwork enclosed by the red plate, like UEM Sunrise's
         * "SUNRISE" and F&N's "F", so it is not a matte and doesn't count
         * against WALL_SURFACE.
         *
         * No `mark` and no opticalScale: like Himalaya Sports, the plate IS the
         * mark, so equal-area sizing on the whole box is correct. Known trade:
         * a solid plate reads heavy. It is 88% ink, so it lands ~2444px² of
         * visible ink on the xl wall, level with Himalaya (2501) and DIALOG
         * (2480), the heaviest logos on this rung, against a ~1100 median. If
         * the client finds it too strong, the lever is an opticalScale (e.g.
         * 0.85); a re-crop would change nothing.
         *
         * 1.33:1 gives a span of 0.45 * sqrt(1.331) = 0.52, far under the
         * binding caps (The Edge Malaysia 2.23 wall / UOB 1.875 strip), so no
         * existing logo resizes.
         */
        logo: "/sponsor-logos/support/sime-darby-property.png",
        width: 1034,
        height: 777
      },
      {
        id: "uem-sunrise",
        name: "UEM Sunrise",
        href: "https://www.uemsunrise.com/",
        /*
         * THE 2026 MASTER, SWOOSH LOOPS INCLUDED, AS SUPPLIED — do not crop the
         * loops off. The client chose this on 2026-09-12 after seeing the numbers
         * below.
         *
         * The supplied file ("UEMS 2026 Logo Master CMYK", kept at
         * ../../../sponsor-source-originals/support/UEMS-logo.pdf) draws three
         * open navy loops around the "UEM ◐ / SUNRISE" wordmark, and the loops
         * take ~56% of the width. Equal-area sizing measures the whole BOX, so
         * the wordmark lands small: box ~66x42px and wordmark ~29px wide on the
         * xl wall; ~22px wide in the desktop footer. Ink covers only 16% of the
         * box, lower than Axiata's 24%, for the same reason: the lockup is
         * mostly air by design.
         *
         * Since 2026-09-22, at client request, it is SIZED BY ITS INNER BLOCK
         * (see `mark` below — "UEM ◐ / SUNRISE", found by eroding away the
         * thin loop strokes): the block is centred and the loops, still as
         * supplied, surround it. opticalScale then matches the tier's median
         * visible ink — the loops are a lot of ink, so it trims: block ~29x16
         * -> ~46x25, whole logo ~66x42 -> ~105x66 on the xl wall.
         *
         *   pdftocairo -png -singlefile -transp -r 100 UEMS-logo.pdf out
         *
         * then crop to the ALPHA channel's bbox (getchannel("A").getbbox(), not
         * the RGBA one — the trap documented for SIME). 100dpi is plenty: the
         * largest render is ~105px wide, so 1182px is ~3.8x a 3x-DPR display. CMYK
         * -> RGB lands the navy at #213765. Genuine alpha. The white "SUNRISE"
         * is opaque artwork inside the navy bar, not a matte.
         *
         * 1.58:1 gives a span of 0.45 * sqrt(1.582) = 0.57, far under the
         * binding caps, so no existing logo resizes.
         */
        logo: "/sponsor-logos/support/uem-sunrise.png",
        width: 1182,
        height: 747,
        // The "UEM ◐ / SUNRISE" block; the loops around it are excluded.
        mark: { x: 360, y: 292, width: 519, height: 281 },
        // Matched to the 0.45 tier's median visible ink (~1100px² on the xl
        // wall), Sep 2026: marked logos read heavy because the extra element
        // adds weight. 0.64 = sqrt(1100 / 2682). Re-measure before changing.
        opticalScale: 0.64
      }
    ]
  },
  {
    // A media category partner: Astro, the Malaysian pay-TV and radio group.
    // Client request, 2026-09-26: this tier sits on the SAME wall row as
    // Official Outdoor TV, to its LEFT, split by a vertical grey rule — see
    // PAIRED_ROWS in SponsorWall.tsx. Its position right after `supporting`
    // pins it to the shared 0.45 rung, exactly like its row partner. The en
    // label "Official TV & Radio" is the client's own wording.
    id: "officialTvRadio",
    sponsors: [
      {
        id: "astro",
        // Trading name, as on the lockup. The registered entity is "Astro
        // Malaysia Holdings Berhad"; the wall uses trading names throughout.
        name: "Astro",
        // www, not the apex: astro.com.my 301s here (same reasoning as AEON).
        href: "https://www.astro.com.my/",
        /*
         * Supplied as "Astro Logo_Full Colour.png" (8000x4500, kept in
         * ../../../sponsor-source-originals/tv/) with the mark filling only a
         * 5001x2222 box — the same baked-in-whitespace trap as The Edge
         * Malaysia and SD Guthrie. Cropped to the ALPHA bbox, then downscaled
         * to 1200 wide: the largest render is ~79px wide, so that is ~5x a
         * 3x-DPR display.
         *
         * The art is ONE flat colour (#EB0082): every pixel, opaque or
         * antialiased, is (235,0,130) and only alpha varies. So only the alpha
         * channel was LANCZOS-resized, and the file is a palette PNG whose 256
         * entries are that colour at alpha 0..255 — an EXACT encoding (0/255
         * error against the RGBA reference), 31KB vs 87KB as RGBA. Re-render
         * the same way if the file is ever replaced with the same kind of art.
         *
         * Genuine alpha, no white matte (0 opaque near-white pixels), so it
         * does not count against WALL_SURFACE.
         *
         * No `mark`: the swoosh is part of Astro's mark, not a separate line
         * like Gas Malaysia's MMC endorsement. No opticalScale either: the file
         * is 43% ink, which lands ~1205px² of visible ink on the xl wall
         * against the 0.45 tier's ~1100 median — inside the normal spread for
         * an unmarked logo.
         *
         * 2.25:1 gives a span of 0.45 * sqrt(2.251) = 0.68, far under the
         * binding caps (The Edge Malaysia 2.23 wall / UOB 1.875 strip), so no
         * existing logo resizes.
         */
        logo: "/sponsor-logos/tv/astro.png",
        width: 1200,
        height: 533
      }
    ]
  },
  {
    // A category partner: PowerScreen, a digital out-of-home screen network.
    // The client placed it after Supporting Partners and BEFORE the Official
    // Hydration Partner, which pins its logo size to their shared 0.45 rung —
    // see TIER_LOGO_SCALE in SponsorWall.tsx before trying to make it bigger.
    // Since 2026-09-26 it shares its wall row with Official TV & Radio, which
    // sits to its left (PAIRED_ROWS in SponsorWall.tsx). The en label
    // "Official Outdoor TV" is the client's own wording.
    id: "officialOutdoorTv",
    sponsors: [
      {
        id: "powerscreen",
        name: "PowerScreen",
        // www, not the apex: powerscreen.com.my serves a different page from
        // www.powerscreen.com.my, and www is the network's main site.
        href: "https://www.powerscreen.com.my/",
        /*
         * Supplied as "PowerScreen Logo_beyondTV.ai" (kept in
         * ../../../sponsor-source-originals/outdoor/): the red screen mark over
         * the "PowerScreen" wordmark and the grey "beyond TV" tagline.
         *
         *   pdftocairo -png -singlefile -transp -r 100 "PowerScreen Logo_beyondTV.ai" out
         *
         * then crop to the ALPHA bbox. 100dpi gives 1081px; the largest render
         * is ~66px wide, so that is ~5x a 3x-DPR display.
         *
         * GENUINE alpha, no white matte. The "beyond TV" tagline is part of the
         * lockup, so it ships as supplied, but it is excluded from sizing via
         * `mark` below (client asked for a larger logo, 2026-09-22).
         *
         * With the mark, the span is 1081 / sqrt(1081 * 531) = 1.43, so
         * 0.45 * 1.43 = 0.64 — far under the binding caps, so no other logo
         * resizes.
         */
        logo: "/sponsor-logos/outdoor/powerscreen.png",
        width: 1081,
        height: 663,
        // The screen emblem + "PowerScreen®" wordmark (ink rows 0–530); the
        // "beyond TV" tagline below the empty band at rows 531–553 is excluded
        // and hangs under the centred lockup. Unlike the other marked logos this
        // needs NO opticalScale: the file is only 32% ink, so marking alone lands
        // it on the 0.45 tier's median visible ink (~1100px² on the xl wall;
        // measured 886 before, 1107 after — sqrt(1100/1107) = 1.00). Re-measure
        // before adding one.
        mark: { x: 0, y: 0, width: 1081, height: 531 }
      }
    ]
  },
  {
    // A category-exclusive partner (the event's drinks supplier). The client
    // placed it between Official Outdoor TV and In-Kind, which pins its logo
    // size to their shared 0.45 rung — see TIER_LOGO_SCALE in SponsorWall.tsx
    // before trying to make it bigger.
    id: "officialHydration",
    sponsors: [
      {
        id: "fn-holdings",
        // Trading name without "Bhd", like the rest of the wall (SD Guthrie,
        // Mah Sing Group). The lockup itself reads "FRASER & NEAVE HOLDINGS BHD".
        name: "Fraser & Neave Holdings",
        // www, not the apex: fn.com.my 301s here (same reasoning as AEON).
        href: "https://www.fn.com.my/",
        /*
         * Supplied as "F&NHB Logo.png" (2000x948, kept in
         * ../../../sponsor-source-originals/hydration/). That filename's "&"
         * and space make a bad URL, hence the rename. The top 65px rows were
         * fully transparent, so it was cropped to the ALPHA bbox (2000x883),
         * then LANCZOS-downscaled (premultiplied) to 1000 wide: 397KB -> 107KB.
         * The largest render is ~94px wide, so 1000 is still ~3.5x a 3x-DPR
         * display.
         *
         * Genuine alpha. The white "F" is opaque artwork enclosed by the black
         * block, part of the mark, not a matte, so it reads the same on any
         * surface.
         *
         * SIZED BY ITS MARK (see `mark` below) since 2026-09-22: the FN block
         * is what gets sized and the "FRASER & NEAVE HOLDINGS BHD" line hangs
         * below. At full mark size it read ~2x its tier's visual weight, so
         * opticalScale trims it to the tier median: mark ~49x28px, whole logo
         * ~94x41px on the xl wall.
         * Mark-sized span: 0.45 * 1000 / sqrt(526 * 304) = 1.13, under the
         * binding caps, so no other logo resizes.
         */
        logo: "/sponsor-logos/hydration/fn-holdings.png",
        width: 1000,
        height: 442,
        // The FN lion block; the company-name line below is excluded.
        mark: { x: 237, y: 0, width: 526, height: 304 },
        // Matched to the 0.45 tier's median visible ink (~1100px² on the xl
        // wall), Sep 2026: marked logos read heavy because the name line adds
        // weight. 0.71 = sqrt(1100 / 2205). Re-measure before changing.
        opticalScale: 0.71
      }
    ]
  },
  {
    // Donated goods/services rather than cash. Sits outside the paid ladder —
    // same footing as `supporting` in SIZE (see TIER_LOGO_SCALE), last in ORDER.
    // Its heading tone mirrors SponsorshipPage's INKIND_TONE so the two pages
    // agree on what in-kind looks like.
    // SINGULAR en label ("In-Kind Sponsor") while this tier holds one sponsor —
    // see the note on `gold` above.
    id: "inKind",
    sponsors: [
      {
        id: "himalaya-sports",
        name: "Himalaya Sports",
        href: "https://himalaya-sports.com/",
        /*
         * THE ONLY PLATED MARK ON THE WALL, AND THAT IS DELIBERATE — do not
         * "fix" it to a transparent lockup.
         *
         * The supplied art ("HIMALAYA SPORTS WHITE WORD & YELLOW LINE", kept in
         * ../../../sponsor-source-originals/in-kind/himalaya-sports.pdf) is a
         * REVERSED lockup: white wordmark + mountain and yellow rules on a
         * full-bleed near-black #100E0D rectangle. The black is a DRAWN opaque
         * object, not page background — the alpha bbox is the whole canvas.
         *
         * That inverts the wall's usual problem. WALL_SURFACE stays pure white
         * because 7 of the other logos are white MATTES; this one renders as a
         * hard-edged black tile against it (square corners — TILE_BASE rounds
         * the <a>, not the <img>). A positive lockup was derived and offered;
         * the client chose to ship the plate as supplied, because it is how the
         * brand presents itself on himalaya-sports.com. Their PDF title implies
         * a dark-word variant exists, so THAT is the escalation if the tile ever
         * reads too heavy — never an opticalScale nudge, which would break tier
         * equality.
         *
         * Equal-area sizing measures the PLATE, not the ~14% ink inside it.
         * Correct here, unlike the Axiata case above: the plate is visible
         * artwork, so the box genuinely IS the mark.
         *
         *   pdftocairo -png -singlefile -transp -r 150 himalaya-sports.pdf out
         *
         * then crop to the ALPHA channel's bbox (getchannel("A").getbbox(), not
         * the RGBA one — same trap documented for SIME). 150dpi, not the 300 the
         * other PDFs used: the largest render anywhere is 74px wide, so 1870px
         * is already ~8x a 3x-DPR display, and 300dpi cost 219KB for no visible
         * gain. Left RGBA — a 255-colour octree pass measured 19/255 error
         * against DCD's accepted 3-6, despite the art being three flat colours,
         * because the antialiased curves carry too many intermediate values.
         *
         * 1.97:1 gives a span of 0.45 * sqrt(1.970) = 0.63, far under the
         * binding caps, so no existing logo resizes.
         */
        logo: "/sponsor-logos/in-kind/himalaya-sports.png",
        width: 1870,
        height: 949
      }
    ]
  }
];

/**
 * Tiers that actually have logos — see SHOW_EMPTY_TIER_SLOTS in SponsorWall.tsx.
 * Includes `organisers`. This is what the /sponsors WALL renders; the footer
 * strip renders `stripSponsorTiers` below.
 */
export const filledSponsorTiers = sponsorTiers.filter((tier) => tier.sponsors.length > 0);

/**
 * What the site-wide footer strip renders: sponsors only.
 *
 * Organisers are excluded at CLIENT REQUEST — they run the event rather than
 * buy into it, and they're already credited in full on /sponsors, which is the
 * page that exists for it. Don't "fix" this back to `filledSponsorTiers`.
 */
export const stripSponsorTiers = filledSponsorTiers.filter((tier) => tier.id !== "organisers");

/**
 * Sponsors only — feeds the ItemList JSON-LD so search/AI crawlers can read the
 * names. Organisers are deliberately EXCLUDED: they run the event, so putting
 * them in a list titled "Sponsors" would misstate the relationship.
 */
export const allSponsors = sponsorTiers
  .filter((tier) => tier.id !== "organisers")
  .flatMap((tier) => tier.sponsors);

/** The organising bodies — mirrored into schema.org `organizer` on the event. */
export const allOrganisers =
  sponsorTiers.find((tier) => tier.id === "organisers")?.sponsors ?? [];

/**
 * Sponsors whose promo video has been delivered, in wall order — this is what
 * /sponsors/videos renders, and the tier travels with each one so the card can
 * carry its tone bar and label.
 *
 * Only 1 of the 5 entitled sponsors has a video up (Mah Sing). Two were
 * delivered and put up first at the CLIENT'S instruction (Sep 2026: "since we
 * currently only have 2 videos, please only put these 2 videos up first"); DCD
 * Technology's was withdrawn on 8 Oct 2026 (see its entry). No "coming soon"
 * cards. Each later video appears here by filling in its `promoVideo` above.
 */
export const sponsorVideos: Array<{ sponsor: Sponsor; tier: SponsorTierId }> = sponsorTiers.flatMap(
  (tier) =>
    tier.sponsors
      .filter((sponsor) => sponsor.promoVideo)
      .map((sponsor) => ({ sponsor, tier: tier.id }))
);
